/**
 * Third-person orbit camera: right-drag to rotate, wheel to zoom, follows the
 * player. Left clicks are left free for click-to-move. Touch (AD-817): a one-finger
 * drag orbits and a two-finger pinch zooms — classified by `tapControls.ts`, which
 * calls `orbitBy` / `zoomBy` here, so both paths share one set of bounds.
 */

import * as THREE from 'three';

import { runTeardown } from './teardown.js';

/**
 * Orbit-camera bounds. The single source of truth for the yaw-free zoom/pitch
 * envelope; the E1.5 capture-mode preset table (game/capture.ts) parameterises
 * poses STRICTLY within these numbers and never widens them — zoom-out range is
 * a visibility-fairness surface (roadmap E1.5 invariant), so a wanted bound
 * change is an escalation, not an in-feature edit.
 */
export const CAMERA_BOUNDS = {
  MIN_DIST: 3,
  MAX_DIST: 18,
  MIN_PITCH: 0.15,
  MAX_PITCH: 1.45,
} as const;

const MIN_DIST = CAMERA_BOUNDS.MIN_DIST;
const MAX_DIST = CAMERA_BOUNDS.MAX_DIST;
const MIN_PITCH = CAMERA_BOUNDS.MIN_PITCH;
const MAX_PITCH = CAMERA_BOUNDS.MAX_PITCH;

/** Where the boom puts the camera, and the point it looks at. */
export interface OrbitPose {
  x: number;
  y: number;
  z: number;
  lookX: number;
  lookY: number;
  lookZ: number;
}

/** How far above a follow target's origin the camera aims (chest height). */
export const ORBIT_LOOK_HEIGHT = 1.1;

/**
 * The third-person boom itself: target + (yaw, pitch, dist) ⇒ camera pose.
 *
 * Extracted from `OrbitCamera.update` (which now delegates to it) so that any
 * other mode needing the GAME'S camera geometry uses this function rather than
 * writing a second copy of the same trigonometry — the dungeon explorer's
 * third-person mode is the first such caller
 * (`q-dungeon-playable-not-preview` half (A)). Pure: no bounds are applied here,
 * because the callers own their own envelopes (`OrbitCamera` clamps to
 * CAMERA_BOUNDS; the explorer clamps to a strictly NARROWER sub-range and then
 * pulls the boom in for occlusion, which is not a zoom-envelope change).
 */
export function orbitPose(
  target: { x: number; y: number; z: number },
  yaw: number,
  pitch: number,
  dist: number,
): OrbitPose {
  const horiz = dist * Math.cos(pitch);
  return {
    x: target.x - Math.sin(yaw) * horiz,
    y: target.y + dist * Math.sin(pitch),
    z: target.z - Math.cos(yaw) * horiz,
    lookX: target.x,
    lookY: target.y + ORBIT_LOOK_HEIGHT,
    lookZ: target.z,
  };
}

export class OrbitCamera {
  readonly camera: THREE.PerspectiveCamera;
  yaw = Math.PI; // start behind the spawn facing +Z
  pitch = 0.8;
  dist = 9;

  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private el: HTMLElement | null = null;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 300);
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 2) return;
    this.dragging = true;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.el?.setPointerCapture(e.pointerId);
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.dragging) return;
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.orbitBy(dx, dy);
  };

  /** Orbit by a drag delta in CSS px (right-drag; a one-finger touch drag). Pitch stays in CAMERA_BOUNDS. */
  orbitBy(dx: number, dy: number): void {
    this.yaw -= dx * 0.0075;
    this.pitch = Math.min(MAX_PITCH, Math.max(MIN_PITCH, this.pitch + dy * 0.005));
  }

  /**
   * Zoom by a factor on the boom distance (a pinch: previous finger span / new span,
   * so spreading the fingers zooms in). Clamped to CAMERA_BOUNDS like the wheel.
   */
  zoomBy(ratio: number): void {
    if (!Number.isFinite(ratio) || ratio <= 0) return;
    this.dist = Math.min(MAX_DIST, Math.max(MIN_DIST, this.dist * ratio));
  }

  private onPointerUp = (e: PointerEvent): void => {
    if (e.button !== 2) return;
    this.dragging = false;
  };

  private onWheel = (e: WheelEvent): void => {
    this.dist = Math.min(MAX_DIST, Math.max(MIN_DIST, this.dist + Math.sign(e.deltaY) * 0.9));
    e.preventDefault();
  };

  private onContextMenu = (e: Event): void => {
    e.preventDefault();
  };

  attach(el: HTMLElement): void {
    this.el = el;
    el.addEventListener('pointerdown', this.onPointerDown);
    el.addEventListener('pointermove', this.onPointerMove);
    el.addEventListener('pointerup', this.onPointerUp);
    el.addEventListener('wheel', this.onWheel, { passive: false });
    el.addEventListener('contextmenu', this.onContextMenu);
  }

  /** Detach all five listeners. Guarded per step (H-258): one element refusing a
   *  removal must not leave the other four attached. The order — including the
   *  `this.el = null` last — is exactly as it was; what is new is that every step
   *  runs and the first refusal is rethrown after the last one. */
  dispose(): void {
    const el = this.el;
    if (!el) return;
    runTeardown([
      () => el.removeEventListener('pointerdown', this.onPointerDown),
      () => el.removeEventListener('pointermove', this.onPointerMove),
      () => el.removeEventListener('pointerup', this.onPointerUp),
      () => el.removeEventListener('wheel', this.onWheel),
      () => el.removeEventListener('contextmenu', this.onContextMenu),
      () => {
        this.el = null;
      },
    ]);
  }

  /**
   * `renderDist` / `renderPitch` (AD-706): per-frame render values for the
   * dungeon run's occlusion — the distance may only SHORTEN the boom and the
   * pitch may only RAISE it (within CAMERA_BOUNDS); `dist`/`pitch`, the
   * player's own zoom and angle, are untouched.
   *
   * `basePitch` (`q-follow-camera-vs-relief`): over terrain relief the pitch the
   * boom starts from is the player's made relative to the slope (`terrainCamera.ts`),
   * so it may sit BELOW `pitch`; it is clamped to CAMERA_BOUNDS, and `renderPitch`
   * still only raises from it.
   */
  update(
    target: THREE.Vector3,
    renderDist?: number,
    renderPitch?: number,
    basePitch?: number,
  ): void {
    const dist = renderDist === undefined ? this.dist : Math.min(this.dist, renderDist);
    const base =
      basePitch === undefined || !Number.isFinite(basePitch)
        ? this.pitch
        : Math.min(MAX_PITCH, Math.max(MIN_PITCH, basePitch));
    const pitch =
      renderPitch === undefined ? base : Math.min(MAX_PITCH, Math.max(base, renderPitch));
    const pose = orbitPose(target, this.yaw, pitch, dist);
    this.camera.position.set(pose.x, pose.y, pose.z);
    this.camera.lookAt(pose.lookX, pose.lookY, pose.lookZ);
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Apply a capture preset pose (E1.5). Pitch/dist are clamped to the same
   * bounds the drag/wheel handlers enforce, so a preset can never move the
   * camera outside the CAMERA_BOUNDS envelope; yaw is a bare angle (no bound).
   */
  applyPose(pose: { yaw: number; pitch: number; dist: number }): void {
    this.yaw = pose.yaw;
    this.pitch = Math.min(MAX_PITCH, Math.max(MIN_PITCH, pose.pitch));
    this.dist = Math.min(MAX_DIST, Math.max(MIN_DIST, pose.dist));
  }

  /** Advance yaw by a radians delta (E1.5 slow-orbit drift). Presentation-only. */
  driftYaw(delta: number): void {
    this.yaw += delta;
  }
}
